import React from 'react';

import { Box, Container, Hidden, Stack, Typography } from '@mui/material';
import { graphql } from 'gatsby';
import { useTranslation } from 'react-i18next';

import useShoppingCartContext from '../../../../context/ShoppingCart/use-shopping-cart-context';
import { ServiceType } from '../../../../domain/common/service';
import {
  TravelClub,
  TravelClubPlan,
  travelClub30DayPlan,
  travelClubAnnualPlan,
  travelClubMonthlyPlan,
  TRAVEL_CLUB_SERVICE_KEY,
} from '../../../../domain/travel-club';
import useLocation from '../../../../hooks/useLocation';
import { TravelClubCard } from './travel-club-card';
import { TravelClubFeaturedCard } from './travel-club-featured-card';

// ********************************************************************************
export const travelClubPriceMapping = (props: any, locale: string) => {
  return {};
};
type Props = {};
export const TravelClubPrice: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { addServiceToCart, cart, openShoppingCart } = useShoppingCartContext();
  const { location } = useLocation();

  // Travel Club is only available in MX and certain regions in the US
  // The service must be included in the "services" array in the location object
  const isTravelClubAvailable = location?.services?.includes(TRAVEL_CLUB_SERVICE_KEY);
  // @ts-ignore
  // NOTE: Only one Travel Club service can be added to the cart at a time
  const isTravelClubInCart = cart.items.some((item) => item?.type === ServiceType.TravelClub);

  // == Handlers ===================================================================
  const handleAddServiceToCart = (plan: TravelClubPlan) => {
    if (!isTravelClubAvailable || isTravelClubInCart) return /*sanity check*/;

    const travelClubItem = new TravelClub(plan);
    addServiceToCart(travelClubItem, travelClubItem.type);
    openShoppingCart();
  };

  // == Render =====================================================================
  return (
    <Box bgcolor="#F6F4F9" py={6}>
      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        <Box
          src="/images/TravelClubPrice/travel-club-logo.svg"
          component="img"
          alt="Travel Club"
          position="absolute"
          top={0}
          left={0}
          width="148px"
          height="34px"
          pl={2}
        />
        <Stack spacing={5} pt={{ xs: '40px', md: 0 }}>
          <Stack spacing={2} alignItems="center">
            <Typography variant="h2" color="#3F2B56" fontFamily="Gotham-Bold" fontSize="32px" align="center">
              {t('travel_club_service.plans.title')}
            </Typography>
            <Typography
              variant="body1"
              color="#302F2F"
              fontSize="18px"
              textAlign="center"
              lineHeight="22px"
              maxWidth="544px"
              mx="auto"
            >
              {t('travel_club_service.plans.description')}
            </Typography>
          </Stack>

          <Hidden lgUp>
            <Stack spacing={5} justifyContent="center" alignItems="center">
              <TravelClubFeaturedCard
                traveClubPlan={travelClub30DayPlan}
                action={isTravelClubAvailable ? () => handleAddServiceToCart(travelClub30DayPlan) : undefined}
              />
              <TravelClubCard traveClubPlan={travelClubMonthlyPlan} />
              <TravelClubCard traveClubPlan={travelClubAnnualPlan} />
            </Stack>
          </Hidden>
          <Hidden lgDown>
            <Box display="flex" alignItems="flex-end" justifyContent="space-between">
              <TravelClubCard traveClubPlan={travelClubMonthlyPlan} />
              <TravelClubFeaturedCard
                traveClubPlan={travelClub30DayPlan}
                action={isTravelClubAvailable ? () => handleAddServiceToCart(travelClub30DayPlan) : undefined}
                addedToCart={isTravelClubInCart}
              />
              <TravelClubCard traveClubPlan={travelClubAnnualPlan} />
            </Box>
          </Hidden>
          <Typography
            variant="body1"
            color="#2D2F31"
            fontSize="18px"
            textAlign="left"
            lineHeight="19.2px"
            fontFamily="Gotham-Bold"
          >
            * {t('travel_club_service.plans.non_refundable')}
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
};

/**
 * GraphQL Query
 */
export const query = graphql`
  fragment TravelClubPrice on StrapiWidgetrels {
    travelclubPrice {
      key: id
      widgetPosition
    }
  }
`;
